// Money is always an integer in a fixed smallest unit, never a float
// (app-engineering §2). Formatting happens at the edge, never here.
//
// Two units, chosen for their two audiences:
//  - Customer-facing prices carry EUR/USD *minor units* (cents): `z.int()`.
//  - The internal cost ledger carries *micro-euros* (1e-6 EUR) so a per-call model
//    cost that is a fraction of a cent survives without ever using a float. The
//    ledger is admin-only (data-model.md run_ledger [admin]); this unit never
//    appears in a tenant-facing schema.
import { z } from "zod";
/** A non-negative integer amount in a currency's minor unit (e.g. cents). Customer-facing. */
export const MinorUnits = z.int().nonnegative();
/** An ISO-4217 currency code (three uppercase letters). */
export const CurrencyCode = z.string().regex(/^[A-Z]{3}$/, "must be an ISO-4217 code");
/**
 * A non-negative integer cost in micro-euros (1e-6 EUR). Admin-only: the source of
 * truth for internal cost accounting, never surfaced to a tenant (invariant 8 - the
 * customer sees value units, never a per-action cost).
 */
export const MicroEur = z.int().nonnegative();
/**
 * D58's internal accounting rate: $1.00 = EUR 0.855. Customer prices are USD; the cost ledger is
 * EUR. Sourced (docs/phase-0/decisions-adr.md D58) and dated, per check 59.
 *
 * Verified 2026-07-25 against docs/nightcrew/22-financial-model.md, which carries the same rate in
 * its input register (`fx.eur_per_usd`). One rate, two places, and the check that keeps them equal
 * is `crew/model/inputs.py` failing its own assertion if the register drifts.
 */
export const EUR_PER_USD = 0.855;
/** Minor units per major unit for every currency the platform prices in. */
const MINOR_PER_MAJOR = 100;
/** Micro-euros per euro. */
const MICRO_PER_EUR = 1_000_000;
/**
 * Convert a customer-facing amount in a currency's minor units into the ledger's micro-euros.
 *
 * THIS FUNCTION EXISTS BECAUSE THE TWO UNITS ARE STRUCTURALLY IDENTICAL AND THE COMPILER CANNOT
 * TELL THEM APART. `MinorUnits` and `MicroEur` are both `z.int().nonnegative()`, so passing one
 * where the other is expected type-checks silently and is wrong by a factor of 10,000. The live
 * example: `apps.monthly_ceiling_minor` is minor units and `checkCeiling(spend, ceilingMicroEur)`
 * takes micro-euros, so a $250 monthly ceiling handed over unconverted reads as EUR 0.025 and pauses
 * the app after its first change. It fails closed, which is the only reason it is a latent bug rather
 * than a live one - and it stays latent only until someone "fixes" the direction.
 *
 * The currency argument is not decoration. A USD ceiling compared against a EUR spend is wrong by
 * 17% before the unit error is even considered, which is the half of this trap that looks right.
 */
export const minorToMicroEur = (minor, currency) => {
    if (!Number.isFinite(minor)) {
        throw new RangeError("minorToMicroEur: amount must be finite");
    }
    const major = minor / MINOR_PER_MAJOR;
    const eur = currency === "EUR" ? major : currency === "USD" ? major * EUR_PER_USD : Number.NaN;
    if (Number.isNaN(eur)) {
        // Not a silent zero and not a guessed rate: an unpriced currency is a configuration error, and
        // guessing here would corrupt the ceiling in the direction that spends money.
        throw new RangeError(`minorToMicroEur: no FX rate for currency ${currency}`);
    }
    return Math.round(eur * MICRO_PER_EUR);
};
//# sourceMappingURL=money.js.map